import { useMemo } from 'react'

import {
  type ActionCreatorsMapObject,
  bindActionCreators
} from '@reduxjs/toolkit'

import { useAppDispatch } from '@web/shared/hooks/use-app-dispatch'

type TActionFn = (...args: never[]) => unknown

type BoundAsyncThunk<Action extends TActionFn> = (
  ...args: Parameters<Action>
) => ReturnType<Action> extends (...args: never[]) => infer Result
  ? Result
  : never

type BoundActions<Actions extends ActionCreatorsMapObject> = {
  [key in keyof Actions]: Actions[key] extends TActionFn
    ? ReturnType<Actions[key]> extends TActionFn
      ? BoundAsyncThunk<Actions[key]>
      : Actions[key]
    : never
}

export const useDispatchedActions = <
  T extends ActionCreatorsMapObject = ActionCreatorsMapObject
>(
  actions: T
): BoundActions<T> => {
  const dispatch = useAppDispatch()

  return useMemo(
    () => bindActionCreators(actions, dispatch) as BoundActions<T>,
    [actions, dispatch]
  )
}
